/**
 * Algolia Theme Adapter
 * Main search adapter that hijacks Shopify search and replaces with Algolia
 * Maintains pixel-perfect theme appearance while providing fast search
 */

class AlgoliaThemeAdapter {
  constructor() {
    // Validate configuration
    if (!window.algoliaConfig || !window.algoliaConfig.enabled) {
      this.log('Algolia not enabled');
      return;
    }

    if (!window.algoliaConfig.appId || !window.algoliaConfig.apiKey) {
      console.error('[Algolia] Missing App ID or API Key');
      return;
    }

    // Store configuration
    this.config = window.algoliaConfig;
    this.themeConfig = this.config.theme;
    this.searchParams = this.config.searchParameters;

    // Initialize search client with logging wrapper
    const baseClient = window.AlgoliaShared.excludeUnlisted(
      algoliasearch(this.config.appId, this.config.apiKey)
    );
    this.searchClient = window.algoliaLogger
      ? window.algoliaLogger.wrapSearchClient(baseClient, 'ThemeAdapter')
      : baseClient;

    // Track current layout (grid/list)
    this.currentLayout = 'grid';

    // Debounce timeout
    this.debounceTimeout = null;

    // Initialize
    this.init();
  }

  /**
   * Initialize Algolia search adapter
   */
  init() {
    this.log('Initializing Algolia search adapter');

    // Set up InstantSearch
    this.setupInstantSearch();

    // Hijack existing search forms
    this.hijackSearchForms();

    // Set up widgets
    this.setupWidgets();

    // Start InstantSearch
    this.search.start();

    // Perform initial search if query exists in URL
    this.performInitialSearch();

    // Preserve theme features
    this.setupThemeIntegration();

    this.log('Algolia search adapter initialized');
  }

  /**
   * Perform initial search from URL query parameter
   */
  performInitialSearch() {
    // Skip on collection pages - they use ?q= for collection-specific filtering
    // which is handled by algolia-collection-page.js
    if (window.location.pathname.includes('/collections/')) {
      this.log('Skipping initial search on collection page');
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q');

    if (query) {
      this.log('Performing initial search for:', query);

      // Set the query in InstantSearch helper and search
      this.search.helper.setQuery(query).search();

      // Also update the search input value
      const searchInputs = document.querySelectorAll(this.themeConfig.selectors.searchInput);
      searchInputs.forEach(input => {
        if (input.value !== query) {
          input.value = query;
        }
      });
    }
  }

  /**
   * Set up InstantSearch instance
   */
  setupInstantSearch() {
    this.search = instantsearch({
      indexName: this.config.indexName,
      searchClient: this.searchClient,
      routing: false, // Disable to keep existing URL structure

      searchFunction: (helper) => {
        const container = document.querySelector(this.themeConfig.selectors.searchResults);

        // Handle empty queries
        if (helper.state.query === '') {
          if (container) {
            container.classList.add('search-results--empty');
          }
          return;
        }

        if (container) {
          container.classList.remove('search-results--empty');
        }

        // Perform search
        helper.search();
      }
    });
  }

  /**
   * Hijack existing Shopify search forms
   */
  hijackSearchForms() {
    const searchForms = document.querySelectorAll(this.themeConfig.selectors.searchForm);

    this.log(`Found ${searchForms.length} search forms to hijack`);

    searchForms.forEach((form) => {
      // Prevent default Shopify search
      form.addEventListener('submit', (e) => {
        e.preventDefault();

        const input = form.querySelector(this.themeConfig.selectors.searchInput);
        if (input && input.value) {
          this.search.helper.setQuery(input.value).search();
        }
      });

      // Connect input to Algolia
      const input = form.querySelector(this.themeConfig.selectors.searchInput);
      if (input) {
        this.connectSearchInput(input);
      }
    });
  }

  /**
   * Connect search input to Algolia with debouncing
   */
  connectSearchInput(inputElement) {
    const searchBox = instantsearch.connectors.connectSearchBox(
      (renderOptions, isFirstRender) => {
        const { query, refine } = renderOptions;

        if (isFirstRender) {
          // Debounced input handler (300ms to match theme)
          inputElement.addEventListener('input', (event) => {
            clearTimeout(this.debounceTimeout);

            this.debounceTimeout = setTimeout(() => {
              refine(event.target.value);
            }, 300);
          });

          // Handle clear button if exists
          const clearButton = inputElement.closest('form')?.querySelector('.search__close, [data-search-close]');
          if (clearButton) {
            clearButton.addEventListener('click', () => {
              refine('');
              inputElement.value = '';
            });
          }
        }

        // Sync input value with Algolia state
        if (inputElement.value !== query) {
          inputElement.value = query;
        }
      }
    );

    this.search.addWidgets([searchBox({})]);
  }

  /**
   * Set up Algolia widgets (hits, pagination, stats)
   */
  setupWidgets() {
    // Find results container
    const resultsContainer = document.querySelector(this.themeConfig.selectors.searchResults);

    if (!resultsContainer) {
      console.warn('[Algolia] No results container found');
      return;
    }

    // Add configure widget for deduplication
    // Note: attributeForDistinct must be configured in Algolia Dashboard, not sent as a search param
    this.search.addWidgets([
      instantsearch.widgets.configure({
        distinct: true,
      })
    ]);

    // Detect current layout
    this.detectLayout();

    // Set up hits widget
    this.setupHitsWidget(resultsContainer);

    // Set up pagination if container exists
    this.setupPaginationWidget();

    // Set up stats widget if container exists
    this.setupStatsWidget();
  }

  /**
   * Detect current layout (grid or list)
   */
  detectLayout() {
    const layoutSwitcher = document.querySelector('layout-switcher, [data-layout-switcher]');
    if (layoutSwitcher) {
      const listButton = layoutSwitcher.querySelector('[data-layout="list"]');
      if (listButton && listButton.classList.contains('active')) {
        this.currentLayout = 'list';
      }

      // Listen for layout changes
      layoutSwitcher.addEventListener('layout:changed', (e) => {
        this.currentLayout = e.detail.layout;
        this.log(`Layout changed to: ${this.currentLayout}`);
        // Re-render hits
        this.search.helper.search();
      });
    }
  }

  /**
   * Set up hits widget for product results
   */
  setupHitsWidget(container) {
    const hits = instantsearch.connectors.connectHits(
      (renderOptions) => {
        const { hits, widgetParams: _widgetParams, results } = renderOptions;

        // Log render event
        if (window.algoliaLogger) {
          window.algoliaLogger.logInstantSearchRender('ThemeAdapter Hits', results);
        }

        // Find or create grid container
        let gridContainer = container.querySelector('.grid, .products-list');
        if (!gridContainer) {
          gridContainer = document.createElement('div');
          gridContainer.className = 'grid grid--uniform grid--view-items products-list products-list--list-grid';
          container.appendChild(gridContainer);
        }

        // Update grid class based on layout
        if (this.currentLayout === 'list') {
          gridContainer.classList.remove('products-list--list-grid');
          gridContainer.classList.add('products-list--list-list');
        } else {
          gridContainer.classList.remove('products-list--list-list');
          gridContainer.classList.add('products-list--list-grid');
        }

        // Render products
        gridContainer.innerHTML = this.renderProducts(hits);

        // Dispatch event for theme features to reinitialize
        this.dispatchHitsRendered();
      }
    );

    this.search.addWidgets([hits({})]);
  }

  /**
   * Set up pagination widget
   */
  setupPaginationWidget() {
    const paginationContainer = document.querySelector(this.themeConfig.selectors.pagination);

    if (paginationContainer) {
      const pagination = instantsearch.widgets.pagination({
        container: paginationContainer,
        scrollTo: '.search__results, .search-results',
        cssClasses: {
          root: 'pagination',
          list: 'pagination__list',
          item: 'pagination__item',
          link: 'pagination__link',
          selectedItem: 'pagination__item--current',
          disabledItem: 'pagination__item--disabled',
          firstPageItem: 'pagination__item--first',
          lastPageItem: 'pagination__item--last',
          previousPageItem: 'pagination__item--previous',
          nextPageItem: 'pagination__item--next'
        }
      });

      this.search.addWidgets([pagination]);
    }
  }

  /**
   * Set up stats widget for result count
   */
  setupStatsWidget() {
    const statsContainer = document.querySelector('.search__count, .collection__toolbar-left, .results-count');

    if (statsContainer) {
      const stats = instantsearch.widgets.stats({
        container: statsContainer,
        templates: {
          text: (data, { html }) => {
            if (data.hasNoResults) {
              return html`<span>No results found</span>`;
            }

            if (data.nbHits === 1) {
              return html`<span>1 result</span>`;
            }

            return html`<span>${data.nbHits.toLocaleString()} results</span>`;
          }
        }
      });

      this.search.addWidgets([stats]);
    }
  }

  /**
   * Render products matching theme's card structure
   */
  renderProducts(hits) {
    return hits.map((hit) => this.renderProductCard(hit)).join('');
  }

  /**
   * Render individual product card matching theme structure
   */
  renderProductCard(product) {
    const variant = product.variants && product.variants[0] ? product.variants[0] : {};

    // Get variant price range from Algolia data
    // Algolia Shopify integration provides: price, compare_at_price, variants_min_price, variants_max_price
    const priceMin = product.variants_min_price ?? product.price_min ?? product.price ?? variant.price ?? 0;
    const priceMax = product.variants_max_price ?? product.price_max ?? product.price ?? priceMin;
    const compareAtPriceMin = product.compare_at_price_min ?? product.compare_at_price ?? variant.compare_at_price ?? 0;
    const compareAtPriceMax = product.compare_at_price_max ?? product.compare_at_price ?? 0;

    // Determine if product has a price range (multiple variant prices)
    const hasRange = priceMax > priceMin;

    // Product is on sale if any compare_at_price is higher than the corresponding price
    const onSale = (compareAtPriceMin > 0 && compareAtPriceMin > priceMin) ||
                   (compareAtPriceMax > 0 && compareAtPriceMax > priceMax);

    const available = product.available !== false;
    const imageUrl = product.image || product.featured_image || '';
    const secondImage = product.images && product.images[1] ? product.images[1] : '';
    const variantCount = product.variant_count || product.variants_count || 1;

    // Grid view card
    if (this.currentLayout === 'grid') {
      return this.renderGridCard(product, {
        variant,
        priceMin,
        priceMax,
        compareAtPriceMin,
        compareAtPriceMax,
        onSale,
        available,
        imageUrl,
        secondImage,
        hasRange,
        variantCount
      });
    }

    // List view card
    return this.renderListCard(product, {
      variant,
      priceMin,
      priceMax,
      compareAtPriceMin,
      compareAtPriceMax,
      onSale,
      available,
      imageUrl,
      secondImage,
      hasRange,
      variantCount
    });
  }

  /**
   * Render grid view product card
   */
  renderGridCard(product, data) {
    const { priceMin, priceMax, compareAtPriceMin, compareAtPriceMax, onSale, available, imageUrl, secondImage, hasRange, variantCount } = data;

    return `
      <div class="grid__item">
        <div class="product-card product-card-style-standard">
          <div class="product-card__wrapper h-full">
            <div class="product-card__image-wrapper${!secondImage ? ' product-card__image-wrapper--main-only' : ''} color-background-1 bg-none">
              <a href="/products/${product.handle}" aria-label="${this.escapeHtml(product.title)}" tabindex="-1">
                  ${imageUrl ? `
                    <div class="media-wrapper product-card__image product-card__image--main" style="--aspect-ratio: 1">
                      <img
                        src="${this.getOptimizedImage(imageUrl, 450)}"
                        srcset="${this.generateSrcset(imageUrl)}"
                        sizes="(min-width: 1200px) 300px, (min-width: 750px) 25vw, 50vw"
                        alt="${this.escapeHtml(product.title)}"
                        loading="lazy"
                      >
                      ${this.getLightbulbLoaderHtml()}
                    </div>
                  ` : `
                    <div class="product-card__image product-card__image--main" style="--aspect-ratio: 1">
                      <svg class="placeholder-svg" viewBox="0 0 525 525" xmlns="http://www.w3.org/2000/svg">
                        <rect fill="#f0f0f0" width="525" height="525"/>
                      </svg>
                    </div>
                  `}
                  ${secondImage ? `
                    <div class="media-wrapper product-card__image product-card__image--second inset-full hidden md:block" style="--aspect-ratio: 1">
                      <img
                        src="${this.getOptimizedImage(secondImage, 450)}"
                        srcset="${this.generateSrcset(secondImage)}"
                        sizes="(min-width: 1200px) 300px, (min-width: 750px) 25vw, 50vw"
                        alt="${this.escapeHtml(product.title)}"
                        loading="lazy"
                      >
                    </div>
                  ` : ''}
              </a>
              ${onSale ? '<div class="product-card__badge">Sale</div>' : ''}
              ${!available ? '<div class="product-card__badge product-card__badge--sold-out">Sold out</div>' : ''}
              ${this.renderWishlistButton(product)}
            </div>

            <div class="product-card__info text-left">
              ${product.vendor ? `
                <div class="product-card__vendor text-sm-extra">
                  <a href="/collections/vendors?q=${encodeURIComponent(product.vendor)}" class="reversed-link">
                    ${this.escapeHtml(product.vendor)}
                  </a>
                </div>
              ` : ''}

              ${product.product_type ? `
                <p class="product-card__type text-sm-extra m-0">
                  <a href="/collections/types?q=${encodeURIComponent(product.product_type)}" class="reversed-link">
                    ${this.escapeHtml(product.product_type)}
                  </a>
                </p>
              ` : ''}

              <h3 class="product-card__title text-pcard-title">
                <a class="reversed-link block" href="/products/${product.handle}">
                  <span class="reversed-link__text">${this.highlightResult(product, 'title')}</span>
                </a>
              </h3>

              <div class="product-card__price">
                ${this.renderPrice(priceMin, priceMax, compareAtPriceMin, compareAtPriceMax, onSale, hasRange)}
              </div>

              ${variantCount > 1 ? `
                <p class="product-card__options text-subtext text-sm m-0">${variantCount} options available</p>
              ` : ''}

              ${this.renderColorSwatches(product)}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render list view product card
   */
  renderListCard(product, data) {
    const { priceMin, priceMax, compareAtPriceMin, compareAtPriceMax, onSale, available, imageUrl, hasRange, variantCount } = data;

    return `
      <div class="grid__item">
        <div class="product-card product-card-list">
          <div class="product-card__image-wrapper">
            <a href="/products/${product.handle}">
              ${imageUrl ? `
                <img
                  src="${this.getOptimizedImage(imageUrl, 300)}"
                  alt="${this.escapeHtml(product.title)}"
                  loading="lazy"
                >
              ` : `
                <div class="product-card__image--placeholder">No image</div>
              `}
            </a>
          </div>

          <div class="product-card__info">
            <h3 class="product-card__title">
              <a href="/products/${product.handle}">
                ${this.highlightResult(product, 'title')}
              </a>
            </h3>

            ${product.vendor ? `
              <div class="product-card__vendor">
                ${this.escapeHtml(product.vendor)}
              </div>
            ` : ''}

            <div class="product-card__price">
              ${this.renderPrice(priceMin, priceMax, compareAtPriceMin, compareAtPriceMax, onSale, hasRange)}
            </div>

            ${variantCount > 1 ? `
              <p class="product-card__options text-subtext text-sm m-0">${variantCount} options available</p>
            ` : ''}

            ${product.body_html_safe ? `
              <div class="product-card__description">
                ${this.escapeHtml(product.body_html_safe.substring(0, 150))}...
              </div>
            ` : ''}

            <div class="product-card__actions">
              <a href="/products/${product.handle}" class="btn btn--primary">
                ${available ? 'View Details' : 'Sold Out'}
              </a>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Render product price with support for price ranges
   * @param {number} priceMin - Minimum variant price
   * @param {number} priceMax - Maximum variant price
   * @param {number} compareAtPriceMin - Minimum compare at price
   * @param {number} compareAtPriceMax - Maximum compare at price
   * @param {boolean} onSale - Whether product has sale pricing
   * @param {boolean} hasRange - Whether product has multiple variant prices
   */
  renderPrice(priceMin, priceMax, compareAtPriceMin, compareAtPriceMax, onSale, hasRange) {
    const formattedPriceMin = this.formatMoney(priceMin * 100);
    const formattedPriceMax = this.formatMoney(priceMax * 100);
    const formattedComparePriceMin = compareAtPriceMin ? this.formatMoney(compareAtPriceMin * 100) : null;
    const formattedComparePriceMax = compareAtPriceMax ? this.formatMoney(compareAtPriceMax * 100) : null;

    // Build price display based on whether there's a range
    let priceDisplay;
    let comparePriceDisplay = null;

    if (hasRange) {
      // Show price range: "$X - $Y"
      priceDisplay = `${formattedPriceMin} - ${formattedPriceMax}`;

      // For sale with range, show compare prices if they differ from sale prices
      if (onSale) {
        if (formattedComparePriceMin && formattedComparePriceMax) {
          if (compareAtPriceMin !== compareAtPriceMax) {
            comparePriceDisplay = `${formattedComparePriceMin} - ${formattedComparePriceMax}`;
          } else {
            comparePriceDisplay = formattedComparePriceMin;
          }
        } else if (formattedComparePriceMin) {
          comparePriceDisplay = formattedComparePriceMin;
        }
      }
    } else {
      // Single price (no range)
      priceDisplay = formattedPriceMin;

      if (onSale && formattedComparePriceMin) {
        comparePriceDisplay = formattedComparePriceMin;
      }
    }

    if (onSale && comparePriceDisplay) {
      return `
        <span class="price price--on-sale">
          <span class="price-item price-item--sale">
            ${priceDisplay}
          </span>
          <s class="price-item price-item--regular">
            ${comparePriceDisplay}
          </s>
        </span>
      `;
    }

    return `
      <span class="price">
        <span class="price-item price-item--regular">
          ${priceDisplay}
        </span>
      </span>
    `;
  }

  /**
   * Render color swatches if available
   */
  renderColorSwatches(product) {
    if (!product.options || !product.options.color) {
      return '';
    }

    const colors = Array.isArray(product.options.color) ? product.options.color : [product.options.color];

    if (colors.length === 0) {
      return '';
    }

    return `
      <div class="product-card__swatches">
        ${colors.slice(0, 5).map(color => `
          <span class="swatch" style="background-color: ${this.getColorHex(color)}" title="${this.escapeHtml(color)}"></span>
        `).join('')}
        ${colors.length > 5 ? `<span class="swatch-more">+${colors.length - 5}</span>` : ''}
      </div>
    `;
  }

  /**
   * Get color hex value from color name
   */
  getColorHex(colorName) {
    const colorMap = {
      'black': '#000000',
      'white': '#FFFFFF',
      'red': '#FF0000',
      'blue': '#0000FF',
      'green': '#008000',
      'yellow': '#FFFF00',
      'orange': '#FFA500',
      'purple': '#800080',
      'pink': '#FFC0CB',
      'brown': '#A52A2A',
      'gray': '#808080',
      'grey': '#808080'
    };

    return colorMap[colorName.toLowerCase()] || '#CCCCCC';
  }

  /**
   * Format money using Shopify's money format
   */
  formatMoney(cents) {
    const moneyFormat = this.themeConfig.moneyFormat || '${{amount}}';
    const amount = (cents / 100).toFixed(2);

    return moneyFormat
      .replace('{{amount}}', amount)
      .replace('{{amount_no_decimals}}', Math.round(cents / 100))
      .replace('{{amount_with_comma_separator}}', amount.replace('.', ','));
  }

  /**
   * Get optimized image URL
   * Supports Shopify CDN resizing and Cloudflare Image Resizing for external URLs
   */
  getOptimizedImage(url, width) {
    if (!url) return '';

    // Shopify CDN image optimization
    if (url.includes('cdn.shopify.com')) {
      return url.replace(/\.(jpg|jpeg|png|gif|webp)/, `_${width}x.$1`);
    }

    // External images - use Cloudflare Image Resizing if enabled
    const cfConfig = window.FoxTheme?.cloudflareConfig;
    if (cfConfig?.enabled && url.startsWith('https://')) {
      const quality = cfConfig.quality || 80;
      const format = cfConfig.format || 'auto';
      const options = `width=${width},quality=${quality},format=${format},fit=scale-down`;

      if (cfConfig.proxyDomain) {
        return `https://${cfConfig.proxyDomain}/cdn-cgi/image/${options}/${url}`;
      } else {
        // Use origin domain with /cdn-cgi/image/ path
        try {
          const urlObj = new URL(url);
          return `${urlObj.origin}/cdn-cgi/image/${options}${urlObj.pathname}`;
        } catch (_e) {
          return url;
        }
      }
    }

    return url;
  }

  /**
   * Generate srcset for responsive images
   */
  generateSrcset(url) {
    if (!url) return '';

    const widths = [165, 355, 450, 710, 900, 1420];
    return widths
      .map(width => `${this.getOptimizedImage(url, width)} ${width}w`)
      .join(', ');
  }

  /**
   * Highlight search results
   */
  highlightResult(hit, attribute) {
    if (hit._highlightResult && hit._highlightResult[attribute]) {
      return hit._highlightResult[attribute].value;
    }
    return this.escapeHtml(hit[attribute] || '');
  }

  /**
   * Escape HTML to prevent XSS
   * Also escapes quotes for safe use in HTML attributes
   */
  escapeHtml(text) {
    if (text == null) return '';
    const div = document.createElement('div');
    div.textContent = text;
    // innerHTML escapes <, >, & but not quotes - we need quotes for attributes
    return div.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  /**
   * Get the lightbulb loader HTML for image placeholders
   * Simple gray bulb outline matching lightbulb-loader.liquid
   * Includes inline styles since CSS may not be loaded on Algolia pages
   */
  getLightbulbLoaderHtml() {
    return `<div class="lightbulb-loader" aria-hidden="true" style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;background:#fff;z-index:1;opacity:0;visibility:hidden;pointer-events:none;">
      <svg viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg" style="width:25%;max-width:60px;min-width:30px;">
        <path class="bulb-glass" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" d="M32 8 C18 8 12 20 12 28 C12 38 20 44 22 48 L42 48 C44 44 52 38 52 28 C52 20 46 8 32 8Z"/>
        <path class="bulb-base" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" d="M22 48 L22 54 L42 54 L42 48 M24 54 L24 58 L40 58 L40 54"/>
        <path class="bulb-filament" fill="none" stroke="#bbb" stroke-width="2" stroke-linecap="round" d="M28 36 L28 28 L32 32 L36 28 L36 36"/>
      </svg>
    </div>`;
  }

  /**
   * Set up theme feature integration
   */
  setupThemeIntegration() {
    // Listen for hits rendered event
    this.search.on('render', () => {
      this.dispatchHitsRendered();
    });
  }

  /**
   * Dispatch hits rendered event for theme features
   */
  dispatchHitsRendered() {
    // Reinitialize theme features after a short delay
    setTimeout(() => {
      this.reinitializeThemeFeatures();

      // Dispatch custom event
      document.dispatchEvent(new CustomEvent('algolia:hits:rendered', {
        detail: { adapter: this }
      }));
    }, 100);
  }

  /**
   * Reinitialize theme features after Algolia renders
   */
  reinitializeThemeFeatures() {
    // Lazy loading images
    if (window.lazySizes) {
      lazySizes.autoSizer.checkElems();
    }

    // Quick view modals
    if (window.theme && window.theme.initQuickView) {
      window.theme.initQuickView();
    }

    // Product cards
    if (window.theme && window.theme.initProductCards) {
      window.theme.initProductCards();
    }

    // Color swatches
    if (window.theme && window.theme.initSwatches) {
      window.theme.initSwatches();
    }

    // Product compare
    if (window.theme && window.theme.initCompare) {
      window.theme.initCompare();
    }

    // Currency converter
    if (window.Currency && window.Currency.convertAll) {
      window.Currency.convertAll(window.Shopify.currency.active, this.themeConfig.moneyFormat);
    }

    // Note: FoxTheme.Motion.inView() requires (element, callback) args.
    // Product card animations are handled by their own custom elements.

    this.log('Theme features reinitialized');
  }

  /**
   * Debug logging
   */
  log(...args) {
    if (this.config && this.config.debug) {
      // eslint-disable-next-line no-console
      console.log('[Algolia Adapter]', ...args);
    }
  }

  /**
   * Render custom wishlist button
   * Bypasses Swish ThemeObserver for better performance (~270ms saved)
   */
  renderWishlistButton(product) {
    // Get the first variant ID if available
    const variantId = product.variants_inventory?.[0]?.variant_id || product.objectID?.split('_')[1] || '';
    const productId = product.id || product.objectID?.split('_')[0] || '';

    return `
      <div class="product-card__wishlist-wrapper">
        <button
          type="button"
          class="wishlist-btn-custom wishlist-btn-custom--small"
          data-wishlist-btn
          data-product-handle="${product.handle}"
          data-variant-id="${variantId}"
          data-product-id="${productId}"
          aria-label="Add to wishlist"
          aria-pressed="false"
        >
          <span class="wishlist-btn-custom__icon wishlist-btn-custom__icon--empty">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18" aria-hidden="true">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </span>
          <span class="wishlist-btn-custom__icon wishlist-btn-custom__icon--filled" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" width="18" height="18">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </span>
          <span class="wishlist-btn-custom__loading" aria-hidden="true">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
              <circle cx="12" cy="12" r="10" stroke-dasharray="31.4" stroke-dashoffset="10"></circle>
            </svg>
          </span>
        </button>
      </div>
    `;
  }
}

// Make class available globally
window.AlgoliaThemeAdapter = AlgoliaThemeAdapter;
